import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Inbox, Send, Briefcase, BellRing, ArrowUpRight, Plus, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHeader, Panel, Empty, StageBadge } from "../components/common";
import { RdvItem } from "../components/RdvItem";
import { ConversionPanel } from "../components/ConversionPanel";
import { ExportDialog } from "../components/ExportDialog";
import { useData } from "../context/DataContext";
import { ETAPES, fmtDate, fmtEUR, isOverdue, isPastDay, nextDeadline } from "../lib/constants";

const Kpi = ({ id, label, value, sub, icon: Icon, to, accent, delay }) => (
  <Link to={to} data-testid={`kpi-${id}`} className="kc-card kc-lift kc-fade group block p-6" style={{ animationDelay: `${delay}ms` }}>
    <div className="flex items-start justify-between">
      <p className="kc-eyebrow">{label}</p>
      <div className="rounded-md p-2" style={{ background: `${accent}14`, color: accent }}><Icon className="h-4 w-4" /></div>
    </div>
    <p data-testid={`kpi-${id}-value`} className="mt-6 font-display text-5xl font-semibold text-[var(--kc-navy)]">{value}</p>
    <p className="mt-2 flex items-center gap-1 text-sm text-[var(--kc-muted)]">{sub}<ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" /></p>
  </Link>
);

export default function Dashboard() {
  const { demandes, rdvs, missions, byId, loading } = useData();
  const { openNewDemande } = useOutletContext();
  const [exportOpen, setExportOpen] = useState(false);
  const nouvelles = demandes.filter((d) => d.etape === "nouveau");
  const propositions = demandes.filter((d) => d.etape === "proposition_envoyee");
  const enCours = missions.filter((m) => m.statut === "en_cours");
  const aFaire = rdvs.filter((r) => !r.fait).sort((a, b) => a.date.localeCompare(b.date));
  const retard = aFaire.filter((r) => isOverdue(r.date));
  const echeances = enCours.map((m) => ({ m, next: nextDeadline(m) })).filter((x) => x.next).sort((a, b) => a.next.echeance.localeCompare(b.next.echeance));
  const today = format(new Date(), "EEEE d MMMM yyyy", { locale: fr });

  if (loading) return <p className="text-[var(--kc-muted)]" data-testid="dashboard-loading">Chargement…</p>;

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={today} title="Tableau de bord" description="Vue d'ensemble de l'activité commerciale et des missions du cabinet."
        actions={<>
          <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="dashboard-export-button"><Download className="mr-1 h-4 w-4" />Exporter</Button>
          <Button onClick={openNewDemande} className="kc-btn-primary" data-testid="dashboard-new-demande-button"><Plus className="mr-1 h-4 w-4" />Nouvelle demande</Button>
        </>} />
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi id="nouvelles-demandes" label="Nouvelles demandes" value={nouvelles.length} sub="À qualifier" icon={Inbox} to="/demandes" accent="#2563EB" delay={0} />
        <Kpi id="propositions-attente" label="Propositions en attente" value={propositions.length} sub={`${fmtEUR(propositions.reduce((s, d) => s + d.montant_estime, 0))} HT en jeu`} icon={Send} to="/demandes" accent="#9C8535" delay={60} />
        <Kpi id="missions-cours" label="Missions en cours" value={enCours.length} sub={`${fmtEUR(enCours.reduce((s, m) => s + m.montant, 0))} HT`} icon={Briefcase} to="/missions" accent="#0F8A5F" delay={120} />
        <Kpi id="relances-retard" label="Relances en retard" value={retard.length} sub={`${aFaire.length} actions planifiées`} icon={BellRing} to="/agenda" accent="#B42318" delay={180} />
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-5">
        <Panel testId="dashboard-agenda-panel" className="xl:col-span-3" title="Prochains rendez-vous & relances"
          action={<Link to="/agenda" className="text-sm text-[var(--kc-gold-dark)] hover:underline" data-testid="dashboard-agenda-link">Tout voir</Link>}>
          <div className="space-y-2.5">
            {aFaire.slice(0, 6).map((r) => <RdvItem key={r.id} rdv={r} compact />)}
            {!aFaire.length && <Empty>Aucune action planifiée.</Empty>}
          </div>
        </Panel>
        <Panel testId="dashboard-echeances-panel" className="xl:col-span-2" title="Échéances des missions"
          action={<Link to="/missions" className="text-sm text-[var(--kc-gold-dark)] hover:underline">Missions</Link>}>
          <ul className="divide-y divide-[var(--kc-line)]">
            {echeances.map(({ m, next }) => (
              <li key={m.id} className="flex items-center gap-4 py-3 first:pt-0" data-testid={`dashboard-echeance-${m.id}`}>
                <div className={`w-14 shrink-0 rounded-md py-1.5 text-center ${isPastDay(next.echeance) ? "bg-[#B42318]/10 text-[#B42318]" : "bg-[var(--kc-sand)] text-[var(--kc-navy)]"}`}>
                  <p className="font-display text-lg font-semibold leading-none">{fmtDate(next.echeance, "dd")}</p>
                  <p className="text-[10px] uppercase">{fmtDate(next.echeance, "MMM")}</p>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-[var(--kc-navy)]">{next.titre}</p>
                  <p className="truncate text-xs text-[var(--kc-muted)]">{byId[m.demande_id]?.entreprise} · {m.titre}</p>
                </div>
              </li>
            ))}
            {!echeances.length && <Empty>Aucune échéance à venir.</Empty>}
          </ul>
        </Panel>
      </div>

      <div className="mt-6"><ConversionPanel demandes={demandes} missions={missions} /></div>

      <div className="mt-6 grid gap-6 xl:grid-cols-5">
        <Panel testId="dashboard-pipeline-panel" className="xl:col-span-2" title="Pipeline commercial">
          <div className="space-y-4">
            {ETAPES.map((e) => {
              const n = demandes.filter((d) => d.etape === e.id).length;
              return (
                <div key={e.id} data-testid={`pipeline-stage-${e.id}`}>
                  <div className="mb-1.5 flex justify-between text-sm"><span className="text-[var(--kc-ink)]">{e.label}</span><span className="font-mono text-[var(--kc-muted)]">{n}</span></div>
                  <div className="h-2 overflow-hidden rounded-full bg-[var(--kc-sand)]">
                    <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${demandes.length ? (n / demandes.length) * 100 : 0}%`, background: e.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
        <Panel testId="dashboard-recent-panel" className="xl:col-span-3" title="Dernières demandes"
          action={<Link to="/demandes" className="text-sm text-[var(--kc-gold-dark)] hover:underline">Toutes les demandes</Link>}>
          <ul className="divide-y divide-[var(--kc-line)]">
            {demandes.slice(0, 5).map((d) => (
              <li key={d.id}>
                <Link to={`/demandes/${d.id}`} className="flex items-center justify-between gap-4 py-3 hover:opacity-80" data-testid={`dashboard-recent-${d.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[var(--kc-navy)]">{d.entreprise}</p>
                    <p className="truncate text-xs text-[var(--kc-muted)]">{d.contact_nom} · {d.besoins.join(", ") || "Besoin à préciser"} · {d.origine}</p>
                  </div>
                  <StageBadge etape={d.etape} />
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
